import { motion, AnimatePresence } from 'framer-motion';
import React, { useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { ArrowRightCircle, X, XCircleIcon } from 'lucide-react';
import clsx from 'clsx';

type DrawerPosition = 'left' | 'right' | 'top' | 'bottom';
type DrawerSize = 'sm' | 'md' | 'lg' | 'xl' |  'xxl' | 'full' | number;

type DrawerProps = {
  /** Content to be displayed inside the drawer */
  children: React.ReactNode;
  /** Controls whether the drawer is open */
  isOpen: boolean;
  /** Callback function when the drawer is closed */
  onClose: () => void;
  /** Position of the drawer */
  position?: DrawerPosition;
  /** Size of the drawer (applies to width for left/right, height for top/bottom) */
  size?: DrawerSize;
  /** Custom height of the drawer (overrides size for height) */
  height?: number | string;
  /** Custom class name for the drawer */
  className?: string;
  /** Custom class name for the overlay */
  overlayClassName?: string;
  /** Show close button */
  showCloseButton?: boolean;
  /** Close on overlay click */
  closeOnOverlayClick?: boolean;
  /** Close on escape key press */
  closeOnEscape?: boolean;
  /** Header content (if not provided, no header will be shown) */
  header?: React.ReactNode;
  /** Footer content */
  footer?: React.ReactNode;
  /** Disable scrolling on body when drawer is open */
  disableBodyScroll?: boolean;
};

const sizeMap: Record<Exclude<DrawerSize, number>, string> = {
  sm: 'w-80 h-96',
  md: 'w-96 h-[32rem]',
  lg: 'w-1/2 h-3/4',
  xl: 'w-3/4 h-5/6',
  xxl:'w-5/6 h-6/7',
  full: 'w-full h-full',
};

const positionClasses: Record<DrawerPosition, string> = {
  left: 'top-0 left-0 h-full',
  right: 'top-0 right-0 h-full',
  top: 'top-0 left-0 w-full',
  bottom: 'bottom-0 left-0 w-full',
};

const positionVariants = (position: DrawerPosition) => ({
  left: { x: '-100%' },
  right: { x: '100%' },
  top: { y: '-100%' },
  bottom: { y: '100%' },
}[position]);

const Drawer = ({
  children,
  isOpen,
  onClose,
  position = 'right',
  size = 'md',
  height,
  className = '',
  overlayClassName = '',
  showCloseButton = true,
  closeOnOverlayClick = true,
  closeOnEscape = true,
  header,
  footer,
  disableBodyScroll = true,
}: DrawerProps) => {
  // Handle escape key press
  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (e.key === 'Escape' && closeOnEscape) {
        onClose();
      }
    },
    [closeOnEscape, onClose]
  );

  // Add/remove event listeners
  useEffect(() => {
    if (isOpen) {
      document.addEventListener('keydown', handleKeyDown);
      if (disableBodyScroll) {
        document.body.style.overflow = 'hidden';
      }
    } else {
      document.removeEventListener('keydown', handleKeyDown);
      if (disableBodyScroll) {
        document.body.style.overflow = 'auto';
      }
    }

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      if (disableBodyScroll) {
        document.body.style.overflow = 'auto';
      }
    };
  }, [isOpen, handleKeyDown, disableBodyScroll]);

  // Calculate size and height styles
  const getSizeStyles = () => {
    const styles: React.CSSProperties = {};

    // Handle width for left/right position or height for top/bottom
    if (position === 'left' || position === 'right') {
      styles.width = typeof size === 'number' ? `${size}px` : undefined;
      if (height) {
        styles.height = typeof height === 'number' ? `${height}px` : height;
      }
    } else {
      styles.height = typeof size === 'number'
        ? `${size}px`
        : height
          ? (typeof height === 'number' ? `${height}px` : height)
          : undefined;
    }

    return styles;
  };

  const sizeClasses = typeof size === 'string' ? sizeMap[size] : '';

  // Render through a portal anchored to <body>. Without this, the Drawer's
  // `fixed inset-0` overlay is relative to whatever ancestor creates a CSS
  // containing block (a parent with transform/filter/perspective — the
  // navbar wrapper does) — leaving the sidebar visible above the overlay.
  // Modal does the same via Headless UI's Dialog → Portal under the hood.
  const tree = (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Overlay — sits one level below the drawer panel, and both must
              be above page-level z-indexes (navbar, tooltips, sticky tables
              that creep up to z-50). The shared Modal component uses
              z-[9999], so we use z-[9998]/z-[9999] here to match the same
              ceiling and avoid the drawer rendering behind navbar/sidebar. */}
          <motion.div
            className={clsx(
              'fixed inset-0 bg-black bg-opacity-70 backdrop-blur-sm z-[9998] transition-opacity duration-300',
              overlayClassName
            )}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={closeOnOverlayClick ? onClose : undefined}
            transition={{ duration: 0.2 }}
          />

          {/* Drawer */}
          <motion.div
            className={clsx(
              'fixed z-[9999] bg-white shadow-xl overflow-hidden flex flex-col rounded-lg',
              positionClasses[position],
              sizeClasses,
              className
            )}
            style={getSizeStyles()}
            initial={positionVariants(position)}
            animate={position === 'left' || position === 'right' ? { x: 0 } : { y: 0 }}
            exit={positionVariants(position)}
            transition={{ type: 'tween', ease: 'easeInOut', duration: 0.2 }}
          >
            {header || showCloseButton ? (
              <div className="flex items-center justify-between">
                {header && <div className="flex-1">{header}</div>}
                {showCloseButton && (
                  <button
                    type="button"
                    onClick={onClose}
                    className="absolute top-2 left-3 rounded-full bg-gray-100 p-[0.5] text-gray-500  focus:outline-none  transition-colors duration-200 z-10"
                    aria-label="Close"
                  >
                    <ArrowRightCircle className="h-7 w-7" />
                  </button>
                )}
              </div>
            ) : null}

            <div className="flex-1 overflow-y-auto p-4 dark:bg-darkBg dark:text-darkBg-mainText">
              {children}
            </div>

            {footer && (
              <div className="border-t border-gray-200">
                {footer}
              </div>
            )}
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );

  // SSR-safe: only portal when document is available (no-op during server render).
  if (typeof document === 'undefined') return tree;
  return createPortal(tree, document.body);
};

export { Drawer };
export type { DrawerProps, DrawerPosition, DrawerSize };